// src/pages/settings/invoices/InvoiceSettingsNumberingPage.tsx
import {
  Badge,
  Card,
  Field,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  VStack,
  chakra,
} from '@chakra-ui/react'
import { HiCheck, HiX } from 'react-icons/hi'

import {
  SwitchRoot,
  SwitchHiddenInput,
  SwitchControl,
  SwitchThumb,
  SwitchThumbIndicator,
} from '@chakra-ui/react'

import { useInvoiceSettingsCtx } from './InvoiceSettingsShell'
import {
  InvoiceNumberTemplateBuilder,
  PreviewTokenRow,
  renderPreview,
} from '@/components/settings/InvoiceNumberTemplateBuilder'
import { SettingsActionBar } from '../SettingsActionBar'
import { SettingsInput } from '../SettingsControls'
import { FormLayout } from '@/components/layout/FormLayout'

export function InvoiceSettingsNumberingPage() {
  const { saving, serverErrors, clearServerError, autoFormik, hasAutoChanged, saveAuto } =
    useInvoiceSettingsCtx()

  const prefix = (autoFormik.values.activityInvoicePrefix ?? '').trim() || 'RE'
  const runningRaw = String(autoFormik.values.activityInvoiceRunningStart ?? '').trim()
  const running = runningRaw ? runningRaw.padStart(4, '0') : '0001'
  const tpl = (autoFormik.values.activityInvoiceTemplate ?? '').trim()
  const effectiveTpl = tpl.length > 0 ? tpl : '{PREFIX}{YYYY}{MM}-{RUNNING}'
  const previewValue = renderPreview(effectiveTpl, { prefix, running })
  const isDefault = tpl.length === 0

  const resetPolicyError =
    serverErrors.activityInvoiceResetPolicy || autoFormik.errors.activityInvoiceResetPolicy

  return (
    <chakra.form
      data-testid="invoice-settings-numbering"
      onSubmit={e => {
        e.preventDefault()
        saveAuto()
      }}
    >
      <fieldset disabled={saving} style={{ width: '100%' }}>
        <FormLayout.Root>
          <FormLayout.Section>
            <FormLayout.Title>Rechnungsnummernserie</FormLayout.Title>
            <FormLayout.Description>
              Hier können Sie Ihre Rechnungsnummernserie konfigurieren. Die Rechnungsnummer wird
              automatisch fortlaufend vergeben und bei jeder neuen Rechnung um +1 erhöht.
            </FormLayout.Description>
          </FormLayout.Section>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            {/* Left */}
            <VStack align="stretch" gap={4}>
              <Flex direction="column" gap={2}>
                <Field.Root>
                  <Field.Label>Serie konfigurieren</Field.Label>
                </Field.Root>

                <InvoiceNumberTemplateBuilder
                  prefix={autoFormik.values.activityInvoicePrefix ?? ''}
                  running={running}
                  value={autoFormik.values.activityInvoiceTemplate ?? ''}
                  onChange={tpl => {
                    autoFormik.setFieldValue('activityInvoiceTemplate', tpl)
                    clearServerError('activityInvoiceTemplate')
                  }}
                />
              </Flex>

              {/* Prefix */}
              <Field.Root
                invalid={
                  !!(serverErrors.activityInvoicePrefix || autoFormik.errors.activityInvoicePrefix)
                }
              >
                <Field.Label>
                  Präfix festlegen{' '}
                  <Badge variant="subtle" size="xs">
                    optional
                  </Badge>
                </Field.Label>
                <SettingsInput
                  value={autoFormik.values.activityInvoicePrefix ?? ''}
                  onChange={e => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
                    autoFormik.setFieldValue('activityInvoicePrefix', value)
                    clearServerError('activityInvoicePrefix')
                  }}
                />
                {(serverErrors.activityInvoicePrefix ||
                  autoFormik.errors.activityInvoicePrefix) && (
                  <Field.ErrorText>
                    {serverErrors.activityInvoicePrefix || autoFormik.errors.activityInvoicePrefix}
                  </Field.ErrorText>
                )}
              </Field.Root>

              {/* Running start */}
              <Field.Root
                invalid={
                  !!(
                    serverErrors.activityInvoiceRunningStart ||
                    autoFormik.errors.activityInvoiceRunningStart
                  )
                }
              >
                <Field.Label>
                  Startwert des Zählers{' '}
                  <Badge variant="subtle" size="xs">
                    optional
                  </Badge>
                </Field.Label>
                <SettingsInput
                  inputMode="numeric"
                  value={autoFormik.values.activityInvoiceRunningStart ?? ''}
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 9)
                    autoFormik.setFieldValue('activityInvoiceRunningStart', value)
                    clearServerError('activityInvoiceRunningStart')
                  }}
                />
                <Field.HelperText>
                  Die nächste Rechnung beginnt mit diesem Zählerwert. Standard: 1
                </Field.HelperText>
                {(serverErrors.activityInvoiceRunningStart ||
                  autoFormik.errors.activityInvoiceRunningStart) && (
                  <Field.ErrorText>
                    {serverErrors.activityInvoiceRunningStart ||
                      autoFormik.errors.activityInvoiceRunningStart}
                  </Field.ErrorText>
                )}
              </Field.Root>

              {/* Continuous toggle */}
              <Field.Root invalid={!!resetPolicyError}>
                <HStack gap="4" align="center">
                  <SwitchRoot
                    checked={autoFormik.values.activityInvoiceResetPolicy === 'CONTINUOUS'}
                    onCheckedChange={(detail: { checked: boolean }) => {
                      const isContinuous = detail.checked
                      autoFormik.setFieldValue(
                        'activityInvoiceResetPolicy',
                        isContinuous ? 'CONTINUOUS' : 'YEARLY'
                      )
                      clearServerError('activityInvoiceResetPolicy')
                      autoFormik.setFieldError('activityInvoiceResetPolicy', undefined)
                    }}
                    disabled={saving}
                    colorPalette="green"
                  >
                    <SwitchHiddenInput />
                    <SwitchControl>
                      <SwitchThumb>
                        <SwitchThumbIndicator fallback={<HiX />}>
                          <HiCheck />
                        </SwitchThumbIndicator>
                      </SwitchThumb>
                    </SwitchControl>
                  </SwitchRoot>

                  <Text>Zähler ist über den Jahreswechsel hinweg fortlaufend</Text>
                </HStack>

                {resetPolicyError && <Field.ErrorText>{resetPolicyError}</Field.ErrorText>}
              </Field.Root>
            </VStack>

            {/* Right Preview */}
            <Card.Root variant="subtle" colorPalette="blue">
              <Card.Header>
                <Card.Title>Vorschau Ihrer Rechnungsnummernserie</Card.Title>
              </Card.Header>
              <Card.Body>
                <Text>{previewValue}</Text>
                <PreviewTokenRow tpl={effectiveTpl} />

                {isDefault && (
                  <Card.Description>
                    Da Sie keine eigene Serie konfiguriert haben, wird das Standardformat
                    verwendet.
                  </Card.Description>
                )}
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
          <SettingsActionBar
            visible={(autoFormik.dirty && hasAutoChanged) || saving}
            onCancel={() => autoFormik.resetForm()}
            onSave={saveAuto}
            saving={saving}
            saveDisabled={!autoFormik.dirty || !autoFormik.isValid || !hasAutoChanged || saving}
            cancelDisabled={saving}
          />
        </FormLayout.Root>
      </fieldset>
    </chakra.form>
  )
}
